import { Spacing } from '@persona-id/design-tokens';
import { ArrowCollapseIcon, ArrowExpandIcon, XIcon } from '@persona-id/design-tokens/icons';
import { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { NAVBAR_COLLAPSED_WIDTH, NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import {
  WorkflowStep,
  WorkflowStepInput,
  WorkflowStepOutput,
  WorkflowVersion,
} from 'dashboard/models/workflow-version';
import { TimelineInputs } from 'dashboard/views/DashboardWorkflowRunsShowView/TimelineInputs';
import WorkflowRunOverallStats from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunOverallStats';
import WorkflowRunTimeline, {
  TimelineViewMode,
} from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunTimeline';
import Button from 'design-system/components/Button';
import { Title } from 'design-system/components/Card/Header';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import useNavbarToggle from 'lib/hooks/useNavbarToggle';
import { TokensToNode } from 'lib/workflows';

const DragHandleContainer = styled.div`
  width: 100%;
  position: absolute;
  top: -5px;
  text-align: center;
  cursor: row-resize;
`;

interface VerticalDragHandlerProps {
  onMoveTo: (y: number) => void;
  onRelease: () => void;
  onDoubleClick: () => void;
}

const VerticalDragHandler: FC<VerticalDragHandlerProps> = ({
  onMoveTo,
  onRelease,
  onDoubleClick,
}) => {
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const resize = (event: MouseEvent) => {
      if (isDragging) {
        onMoveTo(event.clientY);
      }
    };

    const stopDragging = () => {
      setIsDragging(false);
      onRelease();
    };

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopDragging);
    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopDragging);
    };
  }, [isDragging, onMoveTo, onRelease]);
  return (
    <DragHandleContainer
      aria-label='Drag to resize diagnostics panel'
      onMouseDown={(e) => {
        setIsDragging(true);
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseUp={() => {
        setIsDragging(false);
      }}
      onMouseOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDoubleClick={onDoubleClick}
    >
      <StyledIconDrag $active={false} $horizontal={true} width={14} />
    </DragHandleContainer>
  );
};

const TriStatePanel = styled.div.attrs<{
  panelSize: PanelSize;
  navBarCollapsed: boolean;
  customHeight: number | null;
}>(({ panelSize, customHeight }) => ({
  style: {
    height:
      panelSize === 'drawer'
        ? `${customHeight ? `${customHeight}px` : '50%'}`
        : panelSize === 'maximized'
          ? `calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px)`
          : 'auto',
  },
}))<{
  panelSize: PanelSize;
  navBarCollapsed: boolean;
  customHeight: number | null;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  position: relative;
  padding: ${Spacing[4]} ${Spacing[4]} 0 ${Spacing[4]};
  ${({ panelSize }) =>
    (panelSize === 'drawer' || panelSize === 'minimized') &&
    css`
      width: 100%;
      border-top: 1px solid ${({ theme }) => theme.color.border.primary};
    `}

  ${({ panelSize, navBarCollapsed }) =>
    panelSize === 'maximized' &&
    css`
      position: fixed;
      width: calc(100vw - ${navBarCollapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_EXPANDED_WIDTH}px);
      z-index: 1;
    `}
`;

const StyledTitle = styled(Title)`
  flex-grow: 1;
`;

const HiddablePanel = styled.div<{ hidden: boolean }>`
  padding: 0 ${Spacing[1]};
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  ${({ hidden }) => hidden && 'display: none;'}
`;

const TableContainer = styled.div`
  overflow-y: auto;
`;
type PanelSize = 'maximized' | 'drawer' | 'minimized';

interface WorkflowDiagnosticsPanelProps {
  workflowVersion: WorkflowVersion;
  workflowRun: WorkflowRun;
  tokensToSteps: TokensToNode;
  tokensToRunSteps: Record<string, WorkflowRunStep>;
  allRunSteps: WorkflowRunStep[];
  activeNode: WorkflowStepInput | WorkflowStepOutput | WorkflowStep | null;
  selectNode: (token: string | null) => void;
  onExit: () => void;
  updateLoopCycle: (loopCycle: Record<string, number>) => void;
}

const WorkflowDiagnosticsPanel: FC<WorkflowDiagnosticsPanelProps> = ({
  workflowVersion,
  workflowRun,
  tokensToSteps,
  tokensToRunSteps,
  allRunSteps,
  activeNode,
  selectNode,
  onExit,
  updateLoopCycle,
}) => {
  const [panelSize, setPanelSize] = useState<PanelSize>('drawer');
  const [navBarCollapsed] = useNavbarToggle();
  const [height, setHeight] = useState<number | null>(null);
  const [timelineViewMode, setTimelineViewMode] = useState<TimelineViewMode>('fit');
  const [enableSetTimelineViewMode, setEnableSetTimelineViewMode] = useState(true);

  const [searchString, setSearchString] = useState('');
  return (
    <TriStatePanel
      id='workflow-run-diagnostics-panel__panel'
      role='panel'
      panelSize={panelSize}
      navBarCollapsed={navBarCollapsed}
      customHeight={height}
    >
      <VerticalDragHandler
        onMoveTo={(y) => {
          setPanelSize('drawer');
          const { innerHeight } = window;
          // The whole thing looks better with 10 px offset
          setHeight(innerHeight - y + 10);
        }}
        onRelease={() => {
          if (!height) {
            return;
          }
          const { innerHeight } = window;
          if (height > innerHeight * 0.95) {
            setPanelSize('maximized');
            setHeight(innerHeight);
          } else if (height < 80) {
            setPanelSize('minimized');
            setHeight(80);
          }
        }}
        onDoubleClick={() => {
          if (panelSize === 'maximized') {
            setPanelSize('drawer');
            setHeight(null);
          } else {
            setPanelSize('maximized');
            setHeight(window.innerHeight);
          }
        }}
      />
      <HStack>
        <StyledTitle>Run details</StyledTitle>
        {panelSize === 'maximized' ? (
          <Button
            id='workflow-run-diagnostics-panel__drawer-button'
            aria-label='Return the run stats table to the bottom of the workflow run panel'
            width='icon'
            variant='inverse'
            onClick={() => {
              setPanelSize('drawer');
            }}
          >
            <ArrowCollapseIcon />
          </Button>
        ) : (
          <Button
            id='workflow-run-diagnostics-panel__maximize-button'
            aria-label='Maximize the run stats table'
            width='icon'
            variant='inverse'
            onClick={() => {
              setPanelSize('maximized');
            }}
          >
            <ArrowExpandIcon />
          </Button>
        )}
        <Button
          id='workflow-run-diagnostics-panel__close'
          aria-label='Exit run stats view'
          width='icon'
          variant='inverse'
          onClick={onExit}
        >
          <XIcon />
        </Button>
      </HStack>
      <HiddablePanel hidden={panelSize === 'minimized'}>
        <Spacer height={2} />
        <WorkflowRunOverallStats workflowRun={workflowRun} />
        <Spacer height={2} />
        <TimelineInputs
          enableSetTimelineViewMode={enableSetTimelineViewMode}
          onSearch={setSearchString}
          onSetTimelineViewMode={setTimelineViewMode}
        />
        <Spacer height={2} />
        <TableContainer>
          <WorkflowRunTimeline
            workflowVersion={workflowVersion}
            workflowRun={workflowRun}
            tokensToSteps={tokensToSteps}
            tokensToRunSteps={tokensToRunSteps}
            allRunSteps={allRunSteps}
            searchString={searchString}
            viewMode={timelineViewMode}
            activeNode={activeNode}
            selectNode={selectNode}
            updateLoopCycle={updateLoopCycle}
            allowSetViewMode={setEnableSetTimelineViewMode}
          />
        </TableContainer>
      </HiddablePanel>
    </TriStatePanel>
  );
};

export default WorkflowDiagnosticsPanel;
